// api/auth.api.ts
import api from './api'
import type { CreateUserPayload, AuthTokens } from '../types/auth'
import type { User } from '../types/user'

export const authApi = {
  checkEmailAvailable(email: string): Promise<Response> {
    return api.post('/users/check-email/', { email }, { skipAuthHeader: true, skipAuthRefresh: true })
  },

  sendCode(email: string): Promise<Response> {
    return api.post('/users/send-code/', { email })
  },

  verifyCode(email: string, code: string): Promise<Response> {
    return api.post('/users/verify-code/', { email, code })
  },

  register(email: string, payload: Omit<CreateUserPayload, 'repeat_password'>): Promise<AuthTokens> {
    return api.post<AuthTokens>('/users/register/', { email, ...payload }).then((res) => res.data)
  },

  requestForgotPassword(email: string): Promise<{ detail?: string }> {
    return api
      .post('/users/forgot-password/', { email }, { skipAuthHeader: true, skipAuthRefresh: true })
      .then((res) => res.data)
  },

  confirmPasswordReset(uid: string, token: string, newPassword: string): Promise<{ detail?: string }> {
    return api
      .post<{
        detail?: string
      }>(
        '/users/password-reset/confirm/',
        { uid, token, new_password: newPassword },
        { skipAuthHeader: true, skipAuthRefresh: true },
      )
      .then((res) => res.data)
  },

  login(email: string, password: string): Promise<AuthTokens> {
    return api
      .post<AuthTokens>('/api/token/', { email, password }, { skipAuthHeader: true, skipAuthRefresh: true })
      .then((res) => res.data)
  },

  me(): Promise<User> {
    return api.get('/users/me/').then((res) => res.data)
  },

  async updateAvatar(formData: FormData) {
    // Do not set Content-Type — axios must add multipart boundary; manual header breaks uploads.
    return api.patch('/users/me/', formData, {
      timeout: 120_000,
    })
  },

  async updateMe(formData: FormData) {
    return api.patch('/users/me/', formData, {
      timeout: 120_000,
    })
  },

  async updateProfileSettings(payload: Record<string, string | number | boolean | null | undefined>) {
    const formData = new FormData()
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })
    return api.patch('/users/me/', formData, {
      timeout: 120_000,
    })
  },

  changePassword(oldPassword: string, newPassword: string): Promise<{ detail?: string }> {
    return api
      .post<{ detail?: string }>('/users/me/change-password/', {
        old_password: oldPassword,
        new_password: newPassword,
      })
      .then((res) => res.data)
  },

  async removeAvatar() {
    return api.delete('/users/me/avatar/')
  },

  async deleteAccount() {
    return api.delete('/users/me/')
  },

  /** Verify that a social URL is reachable (HTTP 200) from backend. */
  verifySocialUrl(url: string): Promise<{ ok: boolean }> {
    return api.post('/users/me/verify-social-url/', { url }).then((res) => res.data)
  },

  /** Create Stripe Connect Express account for the current user. Returns account_id. */
  createConnectAccount(country?: string): Promise<{ account_id: string }> {
    const body = country ? { country } : {}
    return api.post<{ account_id: string }>('/users/me/connect/create-account/', body).then((res) => res.data)
  },

  /** Live Connect onboarding status from Stripe (details_submitted). */
  getConnectStatus(): Promise<{
    has_account: boolean
    account_id: string | null
    details_submitted: boolean
    charges_enabled: boolean
    payouts_enabled: boolean
    onboarding_complete: boolean
    verification_pending: boolean
    stripe_verified: boolean
    requirements_currently_due?: string[]
    requirements_past_due?: string[]
    requirements_pending_verification?: string[]
  }> {
    return api.get('/users/me/connect/status/').then((res) => res.data)
  },

  /** Get Stripe onboarding link so user can provide DOB, phone, address, identity on Stripe. */
  getConnectOnboardingLink(returnUrl: string, refreshUrl: string): Promise<{ url: string }> {
    return api
      .post<{ url: string }>('/users/me/connect/onboarding-link/', { return_url: returnUrl, refresh_url: refreshUrl })
      .then((res) => res.data)
  },

  /** Get client_secret for Stripe Connect embedded onboarding (iframe). */
  getConnectAccountSession(): Promise<{ client_secret: string }> {
    return api.post<{ client_secret: string }>('/users/me/connect/account-session/').then((res) => res.data)
  },

  /** Get Stripe Connect account balance (available for payouts). */
  getConnectBalance(): Promise<{
    available: Array<{ amount: number; currency: string }>
    pending: Array<{ amount: number; currency: string }>
  }> {
    return api.get('/users/me/connect/balance/').then((res) => res.data)
  },

  /** Create a manual payout (amount in cents, currency e.g. eur). Password required on backend. */
  createPayout(
    amount: number,
    password: string,
    currency?: string,
    forceInstant?: boolean,
  ): Promise<{ id: string; amount: number; currency: string; status: string }> {
    return api
      .post('/users/me/connect/create-payout/', {
        amount,
        password,
        currency: currency || 'eur',
        force_instant: !!forceInstant,
      })
      .then((res) => res.data)
  },

  /** Generate print-ready A4 PDF with QR cards. */
  downloadQrPrintA4Pdf(
    sides: { top: string; right: string; bottom: string; left: string } | string[],
    pages = 1,
  ): Promise<Blob> {
    const params = {
      ...(Array.isArray(sides)
        ? { langs: (sides || []).join(',') }
        : { top: sides.top, right: sides.right, bottom: sides.bottom, left: sides.left }),
      pages,
    }
    return api
      .get('/users/me/qr-print/a4/', {
        params,
        responseType: 'blob',
      })
      .then((res) => res.data as Blob)
  },

  /** Single QR card PDF (900×900 pt) with four side phrases — same query params as A4 (no pages). */
  downloadQrPrintSquarePdf(
    sides: { top: string; right: string; bottom: string; left: string } | string[],
  ): Promise<Blob> {
    const params = {
      ...(Array.isArray(sides)
        ? { langs: (sides || []).join(',') }
        : { top: sides.top, right: sides.right, bottom: sides.bottom, left: sides.left }),
    }
    return api
      .get('/users/me/qr-print/square/', {
        params,
        responseType: 'blob',
      })
      .then((res) => res.data as Blob)
  },
}

export function refreshToken(refresh: string): Promise<AuthTokens> {
  return api.post('/api/token/refresh/', { refresh })
}
